import { Link } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { 
  FileText, BookOpen, Calculator, CreditCard, ArrowRight, 
  Shield, Briefcase, GraduationCap
} from "lucide-react";

const guides = [
  {
    id: "permessi-lavoro",
    title: "Guida ai Permessi Svizzeri: G, L e B per Lavoratori in Ticino",
    description: "Scopri le differenze tra Permesso G (Frontalieri), L (Breve termine) e B (Residenti). Tabella comparativa e requisiti completi.",
    icon: Shield,
    color: "bg-blue-500",
    keywords: ["Permesso G Ticino", "Frontalieri Svizzera", "Permessi di lavoro"],
    readTime: "8 min"
  },
  {
    id: "cv-svizzero",
    title: "Come scrivere un Curriculum efficace per le aziende ticinesi",
    description: "Gli elementi obbligatori del CV svizzero: foto professionale, certificati di lavoro e competenze linguistiche. I 3 errori da evitare.",
    icon: FileText,
    color: "bg-emerald-500",
    keywords: ["Modello CV Ticino", "Curriculum Svizzera", "CV professionale"],
    readTime: "6 min"
  },
  {
    id: "stipendio-netto",
    title: "Stipendio in Ticino: Dal Lordo al Netto e l'Imposta alla Fonte",
    description: "Calcola il tuo stipendio netto in Svizzera. Guida alle trattenute AVS, LPP e imposta alla fonte per lavoratori stranieri.",
    icon: Calculator,
    color: "bg-amber-500",
    keywords: ["Stipendio Netto Svizzera", "Imposta alla fonte", "Calcolo stipendio Ticino"],
    readTime: "7 min"
  }
];

export default function GuidesPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* SEO Meta - handled by React Helmet or similar in production */}
      
      <Navbar />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-[#004E64] to-[#006B8A] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 mb-4">
              <BookOpen className="w-6 h-6" />
              <span className="text-sm font-medium text-white/80 uppercase tracking-wider">
                Centro Risorse
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              Guide e Risorse per<br />
              <span className="text-[#DA291C]">Lavorare in Ticino</span>
            </h1>
            <p className="text-lg text-white/80 max-w-2xl">
              Informazioni pratiche e aggiornate su permessi di lavoro, curriculum vitae 
              e stipendi in Canton Ticino. Tutto quello che devi sapere per lavorare in Svizzera.
            </p>
          </div>
        </div>
      </div>

      {/* Guides Grid */}
      <section className="py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {guides.map((guide) => {
              const IconComponent = guide.icon;
              return (
                <Card 
                  key={guide.id} 
                  className="group hover:shadow-xl transition-all duration-300 border-2 hover:border-[#004E64]/30 overflow-hidden"
                >
                  <CardContent className="p-0">
                    {/* Card Header */}
                    <div className={`${guide.color} p-6 text-white`}>
                      <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center mb-4">
                        <IconComponent className="w-7 h-7" />
                      </div>
                      <h2 className="text-xl font-bold leading-tight mb-2 group-hover:underline decoration-2 underline-offset-4">
                        {guide.title}
                      </h2>
                      <div className="flex items-center gap-3 text-sm text-white/80">
                        <span className="flex items-center gap-1">
                          <BookOpen className="w-4 h-4" />
                          {guide.readTime} lettura
                        </span>
                      </div>
                    </div>
                    
                    {/* Card Body */}
                    <div className="p-6">
                      <p className="text-slate-600 mb-4 leading-relaxed">
                        {guide.description}
                      </p>
                      
                      {/* Keywords */}
                      <div className="flex flex-wrap gap-2 mb-6">
                        {guide.keywords.map((keyword, i) => (
                          <span 
                            key={i} 
                            className="text-xs px-2.5 py-1 bg-slate-100 text-slate-600 rounded-full"
                          >
                            {keyword}
                          </span>
                        ))}
                      </div>
                      
                      <Link to={`/guide/${guide.id}`}>
                        <Button className="w-full bg-[#004E64] hover:bg-[#003B4D] text-white group-hover:bg-[#DA291C] transition-colors">
                          Leggi Guida <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Trust Us */}
      <section className="py-12 bg-slate-50 border-y">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">
              Perché scegliere le nostre guide?
            </h2>
            <p className="text-slate-600 mt-2">Contenuti creati da esperti del mercato del lavoro ticinese</p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div className="text-center p-6">
              <div className="w-12 h-12 bg-[#004E64]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <GraduationCap className="w-6 h-6 text-[#004E64]" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Informazioni Verificate</h3>
              <p className="text-sm text-slate-600">Dati aggiornati alle ultime normative svizzere e ticinesi</p>
            </div>
            <div className="text-center p-6">
              <div className="w-12 h-12 bg-[#DA291C]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Briefcase className="w-6 h-6 text-[#DA291C]" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Esperienza Locale</h3>
              <p className="text-sm text-slate-600">Conosciamo il mercato del lavoro del Canton Ticino</p>
            </div>
            <div className="text-center p-6">
              <div className="w-12 h-12 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <CreditCard className="w-6 h-6 text-emerald-600" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">100% Gratuito</h3>
              <p className="text-sm text-slate-600">Tutte le guide sono accessibili senza registrazione</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">
            Pronto a trovare lavoro in Ticino?
          </h2>
          <p className="text-slate-600 mb-8 max-w-2xl mx-auto">
            Metti in pratica quello che hai imparato. Esplora le ultime offerte di lavoro 
            pubblicate dalle aziende del Canton Ticino.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/jobs">
              <Button size="lg" className="bg-[#DA291C] hover:bg-[#B91C1C] text-white px-8">
                <Briefcase className="w-5 h-5 mr-2" />
                Vedi Annunci di Lavoro
              </Button>
            </Link>
            <Link to="/register?type=candidate">
              <Button size="lg" variant="outline" className="border-[#004E64] text-[#004E64] px-8">
                Iscriviti Gratis
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
